import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, ChevronDown } from 'lucide-react';
import { SERVICES, INDUSTRIES } from '../mock';
import ServiceIcon from './ServiceIcon';

const NAV_ITEMS = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services', dropdown: 'services' },
  { label: 'Industries', path: '/industries', dropdown: 'industries' },
  { label: 'Locations', path: '/locations' },
  { label: 'Case Studies', path: '/case-studies' },
  { label: 'Blog', path: '/blog' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); setHoveredMenu(null); }, [location.pathname]);

  return (
    <header className='fixed top-4 left-0 right-0 z-50 px-4'>
      <div className={`max-w-7xl mx-auto backdrop-blur-xl border rounded-full px-5 md:px-6 py-3 transition-all duration-300 ${scrolled ? 'bg-surface/90 border-brand/30 shadow-lg shadow-brand/10' : 'bg-surface/80 border-brand/20'}`}>
        <div className='flex items-center justify-between'>
          <Link to='/' className='flex items-center space-x-3 group'>
            <div className='w-12 h-12 rounded-xl overflow-hidden'>
              <img src='/logo.png' alt='Ransen' className='w-full h-full object-contain logo-motion' />
            </div>
            <div>
              <div className='text-xl font-heading font-bold text-white tracking-tight leading-none'>RANSEN</div>
              <div className='text-[9.5px] text-white/60 tracking-[0.18em] uppercase mt-1'>AI-Driven Growth Agency</div>
            </div>
          </Link>

          <div className='hidden lg:flex items-center gap-1'>
            {NAV_ITEMS.map((l) => (
              <div
                key={l.path}
                className='relative'
                onMouseEnter={() => l.dropdown && setHoveredMenu(l.dropdown)}
                onMouseLeave={() => l.dropdown && setHoveredMenu(null)}
              >
                <NavLink
                  to={l.path}
                  end={l.path === '/'}
                  className={({ isActive }) =>
                    `px-3.5 py-2 rounded-full text-[13px] font-medium transition-colors inline-flex items-center gap-1 ${isActive ? 'text-white bg-white/10' : 'text-white/70 hover:text-white'}`
                  }
                >
                  {l.label}
                  {l.dropdown && <ChevronDown className={`w-3.5 h-3.5 transition-transform ${hoveredMenu === l.dropdown ? 'rotate-180' : ''}`} />}
                </NavLink>

                {/* Dropdown */}
                {l.dropdown === 'services' && hoveredMenu === 'services' && (
                  <DropdownPanel>
                    <div className='grid grid-cols-2 gap-1.5'>
                      {SERVICES.map((s) => (
                        <Link key={s.slug} to={`/services/${s.slug}`} className='flex items-start gap-3 p-3 rounded-lg hover:bg-brand/10 transition-colors group'>
                          <div className='w-9 h-9 shrink-0 rounded-lg bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center shadow-md shadow-brand/30'>
                            <ServiceIcon name={s.icon} className='w-4 h-4 text-white' />
                          </div>
                          <div>
                            <div className='font-heading text-[13px] font-bold text-white group-hover:text-brand transition-colors'>{s.title}</div>
                            <div className='text-white/50 text-[11.5px] leading-snug mt-0.5 line-clamp-2'>{s.short}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </DropdownPanel>
                )}

                {l.dropdown === 'industries' && hoveredMenu === 'industries' && (
                  <DropdownPanel narrow>
                    <div className='grid grid-cols-2 gap-1.5'>
                      {INDUSTRIES.map((i) => (
                        <Link key={i.slug} to={`/industries/${i.slug}`} className='flex items-center gap-3 p-2.5 rounded-lg hover:bg-brand/10 transition-colors group'>
                          <div className='w-8 h-8 shrink-0 rounded-lg bg-brand/20 flex items-center justify-center'>
                            <ServiceIcon name={i.icon} className='w-4 h-4 text-brand' />
                          </div>
                          <div className='font-heading text-[12.5px] font-bold text-white group-hover:text-brand transition-colors leading-tight'>{i.title}</div>
                        </Link>
                      ))}
                    </div>
                  </DropdownPanel>
                )}
              </div>
            ))}
          </div>

          <div className='flex items-center gap-2'>
            <Link to='/contact' className='hidden sm:inline-flex items-center gap-1.5 bg-white text-brand-dark hover:bg-white/90 font-semibold text-[13px] px-5 py-2 rounded-full shadow-lg transition-colors'>
              Get Started
              <ArrowRight className='w-4 h-4' />
            </Link>
            <button className='lg:hidden p-2 text-white' onClick={() => setOpen(!open)} aria-label='Toggle menu'>
              {open ? <X className='w-5 h-5' /> : <Menu className='w-5 h-5' />}
            </button>
          </div>
        </div>

        {open && (
          <div className='lg:hidden mt-4 pt-4 pb-2 border-t border-white/10 flex flex-col gap-1 max-h-[70vh] overflow-y-auto'>
            {NAV_ITEMS.map((l) => (
              <NavLink key={l.path} to={l.path} end={l.path === '/'} className={({ isActive }) => `px-4 py-2.5 rounded-lg text-sm font-medium ${isActive ? 'bg-brand/15 text-white' : 'text-white/75 hover:bg-white/5'}`}>
                {l.label}
              </NavLink>
            ))}
            <Link to='/contact' className='mt-3 btn-primary text-white text-sm font-semibold px-4 py-3 rounded-full text-center'>
              Book a Strategy Call
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}

function DropdownPanel({ children, narrow }) {
  return (
    <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 ${narrow ? 'w-[540px]' : 'w-[680px]'}`}>
      <div className='bg-surface/95 backdrop-blur-xl border border-brand/30 rounded-2xl shadow-2xl shadow-brand/20 p-4'>
        {children}
      </div>
    </div>
  );
}
